import React from "react";
import Foundation from "@fast/fast-components-foundation-react";
import { classNames } from "@microsoft/fast-web-utilities";
import { ContentCardLayout, } from "./content-card.props";
class ContentCard extends Foundation {
    constructor(props) {
        super(props);
        this.handledProps = {
            headingLevel: void 0,
            href: void 0,
            imagePriority: void 0,
            layout: void 0,
            managedClasses: void 0,
            target: void 0,
        };
        this.state = {
            hasAbstract: false,
            isImagePriority: this.props.imagePriority || false,
            hoverActionFocused: false,
        };
        if (this.hasElementSlot("hover-actions")) {
            this.hoverActionsRef = React.createRef();
        }
    }
    /**
     * Renders the component
     */
    render() {
        return (React.createElement("div", Object.assign({}, this.unhandledProps(), { className: this.generateClassNames() }),
            React.createElement("span", { className: this.props.managedClasses.contentCard_backgroundImage }, this.withSlot("background-image")),
            React.createElement("div", { className: this.props.managedClasses.contentCard_mask }),
            this.renderBodyContent(),
            this.props.layout === ContentCardLayout.default
                ? this.generateFooterArea()
                : null,
            this.renderHoverActionsSlot()));
    }
    componentDidMount() {
        // focus-within is not supported for Edge legacy, so I have to setup listeners to handle focus events triggered in child elements
        if (this.hoverActionsRef && this.hoverActionsRef.current) {
            this.hoverActionsRef.current.addEventListener("focusin", () => {
                this.setState({ hoverActionFocused: true });
            });
            this.hoverActionsRef.current.addEventListener("focusout", () => {
                this.setState({ hoverActionFocused: false });
            });
            // mouseleave added for the case you focused on action while the mouse is hovering on actions, you'll want to still hide actions when
            // the mouse leaves the hover actions region.
            this.hoverActionsRef.current.addEventListener("mouseleave", () => {
                this.setState({ hoverActionFocused: false });
            });
        }
    }
    /**
     * Generates class names
     */
    generateClassNames() {
        const { contentCard, contentCard_imagePriority, } = this.props.managedClasses;
        return super.generateClassNames(classNames(contentCard, [this.getClassNameByLayout(), !!this.props.layout], [contentCard_imagePriority, this.state.isImagePriority]));
    }
    getClassNameByLayout() {
        const { contentCard__default, contentCard__condensed, contentCard__infoPane, contentCard__infoPaneSplitHorizontal, contentCard__infoPaneSplitVertical, } = this.props.managedClasses;
        switch (this.props.layout) {
            case ContentCardLayout.default:
                return contentCard__default;
            case ContentCardLayout.condensed:
                return contentCard__condensed;
            case ContentCardLayout.infoPane:
                return contentCard__infoPane;
            case ContentCardLayout.infoPaneSplitHorizontal:
                return contentCard__infoPaneSplitHorizontal;
            case ContentCardLayout.infoPaneSplitVertical:
                return contentCard__infoPaneSplitVertical;
            default:
                return contentCard__default;
        }
    }
    /**
     * Create body class names
     */
    generateBodyClassNames() {
        const { contentCard_body, contentCard__hasAbstract, } = this.props.managedClasses;
        return classNames(contentCard_body, [
            contentCard__hasAbstract,
            this.hasElementSlot("abstract"),
        ]);
    }
    renderBodyContent() {
        const { contentCard_attribution, contentCard_heading, contentCard_headingContainer, contentCard_headingWrapper, contentCard_abstract, } = this.props.managedClasses;
        return (React.createElement("div", { className: this.generateBodyClassNames() },
            this.renderMediaWrapper(),
            React.createElement("div", { className: contentCard_headingContainer },
                React.createElement("span", { className: contentCard_attribution }, this.withSlot("attribution")),
                React.createElement("span", { className: contentCard_headingWrapper, role: "heading", "aria-level": this.props.headingLevel },
                    React.createElement("a", { className: contentCard_heading, href: this.props.href, target: this.props.target }, this.withoutSlot([
                        "start-actions",
                        "hover-actions",
                        "end-actions",
                        "background-image",
                        "media",
                        "icon",
                        "attribution",
                        "abstract",
                        "call-to-action",
                    ])))),
            React.createElement("div", { className: contentCard_abstract }, this.withSlot("abstract")),
            this.props.layout !== ContentCardLayout.default
                ? this.generateFooterArea()
                : null));
    }
    renderMediaWrapper() {
        if (this.hasElementSlot("media") || this.hasElementSlot("icon")) {
            return (React.createElement("div", { className: this.props.managedClasses.contentCard_mediaWrapper },
                this.renderMediaSlot(),
                this.renderIconSlot()));
        }
    }
    renderIconSlot() {
        if (this.hasElementSlot("icon")) {
            return (React.createElement("span", { className: this.props.managedClasses.contentCard_icon }, this.withSlot("icon")));
        }
    }
    renderHoverActionsSlot() {
        if (this.hasElementSlot("hover-actions")) {
            return (
            // toggle opacity instead of display because you cannot tab to elements when display is none
            React.createElement("span", { ref: this.hoverActionsRef, className: this.props.managedClasses.contentCard_hoverActions, style: this.state.hoverActionFocused ? { opacity: "1" } : null }, this.withSlot("hover-actions")));
        }
    }
    renderMediaSlot() {
        if (this.hasElementSlot("media")) {
            return (React.createElement("div", { className: this.props.managedClasses.contentCard_media }, this.withSlot("media")));
        }
    }
    hasElementSlot(slot) {
        if (!this.props.children) {
            return false;
        }
        const elementsWithSlot = this.withSlot(slot);
        return (React.Children.toArray(elementsWithSlot).filter((child) => child !== null).length > 0);
    }
    /**
     * Create footer class names
     */
    generateFooterClassNames() {
        const { contentCard_footer, contentCard_footer__hidden, contentCard__hasAbstractFooter, } = this.props.managedClasses;
        return classNames(contentCard_footer, [
            contentCard_footer__hidden,
            this.hasElementSlot("footer") ||
                (!this.hasElementSlot("start-actions") &&
                    !this.hasElementSlot("end-actions")),
        ], [contentCard__hasAbstractFooter, this.hasElementSlot("abstract")]);
    }
    generateFooterArea() {
        const { contentCard_startActions, contentCard_endActions, } = this.props.managedClasses;
        return (React.createElement("div", { className: this.generateFooterClassNames() },
            React.createElement("span", { className: contentCard_startActions }, this.withSlot("start-actions")),
            React.createElement("span", { className: contentCard_endActions }, this.withSlot("end-actions"))));
    }
}
ContentCard.displayName = "ContentCard";
ContentCard.defaultProps = {
    headingLevel: 2,
    layout: ContentCardLayout.default,
};
export default ContentCard;
export * from "./content-card.props";
